/**
 * useSendComposedEmail — send a user-composed email through the existing
 * gmail_send action pipeline.
 *
 * A human-composed email reuses the exact backend send path the AI uses: we
 * create a proposal carrying a single `gmail_send` action (pre-filled by the
 * user rather than the agent), then immediately approve it. Approval runs the
 * provider-agnostic SendEmailActionHandler → MailboxAPI (Gmail or Outlook) and
 * records the `ahoy_email_sent` timeline event — nothing about "how mail leaves
 * the building" is re-implemented here.
 *
 * When the composer's AI chat was used, a pending proposal already exists (the
 * chat operates on it via the proposal-discussion agent). In that case Send
 * flushes the final editor content onto the action and approves the SAME
 * proposal — passing the payload as an approval stale-guard so a concurrent
 * agent edit turns into a 409 rather than sending stale content.
 *
 * The proposal is tagged with a distinct `source_type_name` so human sends are
 * honest in the resolved-proposals view and in reporting, and are never
 * mistaken for agent suggestions. Because `ahoy_compose` is an interactive
 * channel, a proposal that failed to approve never surfaces in the Actions
 * inbox — so if approval fails after create() succeeds, we reject the
 * orphaned proposal ourselves to guarantee no pending draft lingers.
 *
 * Retry safety: if approve() actually succeeded on the backend but the HTTP
 * response was lost (network blip, tab suspend), our best-effort reject()
 * will fail because the proposal is no longer pending — and without more
 * care, a second Send click would call create() again and send a real
 * duplicate email. To prevent that, the proposal created by an attempt is
 * tracked in `pendingAttemptRef` across calls. A retry with the same content
 * reuses that proposal (calling approve() again, never create()) and lets
 * the backend's own approve_and_execute state check be the source of truth.
 * A retry with different content (the user edited the email) discards the
 * stale proposal and starts fresh, since reusing it would send the wrong
 * content.
 */

import { useCallback, useRef } from 'react';

import { logger } from '@/lib/logger';
import { ProposalClient } from '@/lib/proposals/core/ProposalClient';
import type {
  CreateProposalRequest,
  GmailSendParams,
  ProposalResolution,
} from '@/lib/proposals/core/types';

/**
 * Distinct provenance tag for human-composed sends. Free-form `source_type_name`
 * (no backend whitelist); kept distinct from AI conversation sources so these
 * are filterable/attributable separately.
 */
export const MANUAL_COMPOSE_SOURCE_TYPE = 'manual_compose';

/**
 * Trigger channel for a human-composed email. Must be an allow-listed channel
 * (see apps/actions/repositories/types.py::ALLOWED_CHANNELS) and is one of the
 * INTERACTIVE_CHANNELS so a composer proposal (pending or create-then-approve)
 * never surfaces in the Actions inbox or fires a self-notification.
 */
export const MANUAL_COMPOSE_CHANNEL = 'ahoy_compose';

/**
 * Backend-only placeholders. Proposal create/update validates SendEmailParams
 * (subject min-length 1, body non-empty), but the AI chat may start from an
 * empty draft. These go into the proposal payload only — never into the editor
 * — and the agent's first full-payload rewrite replaces them.
 */
export const EMPTY_DRAFT_SUBJECT_PLACEHOLDER = 'Draft';
export const EMPTY_DRAFT_BODY_PLACEHOLDER =
  '(empty draft — write it based on my instructions)';

/** The email content to send or sync (a new email — no reply threading). */
export interface ComposedEmail {
  to: string[];
  subject: string;
  /** Plain-text body. Required non-empty for a new (non-reply) email. */
  body: string;
  cc?: string[];
  bcc?: string[];
  body_html?: string;
  attachment_file_ids?: string[];
  social_account_id?: number;
  disable_tracking?: boolean;
  /**
   * Editor signal for signature state. Set by the compose panel when a signature
   * was seeded: "inserted" when still present, "user_removed" when explicitly deleted.
   * Omit (or "none") when no signature was ever seeded for this draft.
   */
  signature_disposition?: 'none' | 'inserted' | 'user_removed';
  /** CRM entity id of the recipient, linked into the proposal for provenance. */
  recipientEntityId?: string;
  /**
   * When the AI chat already created a pending proposal for this draft, Send
   * updates + approves it instead of creating a new one. Both ids come from
   * useComposeProposalChat.
   */
  proposalId?: number;
  actionId?: number;
}

/**
 * Map composer content → gmail_send action_params. The single construction
 * site shared by Send and the AI-chat proposal sync, so the payload the agent
 * edits and the payload that gets sent are built identically.
 *
 * Normalizes empty cc/bcc/attachments to `undefined` (matching what
 * emailContentFromGmailSend reads back — keeps dirty-comparison stable). With
 * `placeholders`, empty subject/body are substituted so proposal validation
 * passes on a not-yet-written draft.
 */
export function buildGmailSendParams(
  email: ComposedEmail,
  options?: { placeholders?: boolean }
): GmailSendParams {
  // Trim only to test emptiness — non-empty values pass through unaltered so
  // the direct Send path stays byte-identical to what the user wrote.
  const hasSubject = email.subject.trim().length > 0;
  const hasBody = email.body.trim().length > 0;
  return {
    to: email.to,
    subject:
      !hasSubject && options?.placeholders
        ? EMPTY_DRAFT_SUBJECT_PLACEHOLDER
        : email.subject,
    body:
      !hasBody && options?.placeholders
        ? EMPTY_DRAFT_BODY_PLACEHOLDER
        : email.body,
    cc: email.cc && email.cc.length > 0 ? email.cc : undefined,
    bcc: email.bcc && email.bcc.length > 0 ? email.bcc : undefined,
    body_html: email.body_html || undefined,
    attachment_file_ids:
      email.attachment_file_ids && email.attachment_file_ids.length > 0
        ? email.attachment_file_ids
        : undefined,
    social_account_id: email.social_account_id,
    disable_tracking: email.disable_tracking,
    signature_disposition: email.signature_disposition,
  };
}

/**
 * Build the create-proposal request for a composer draft — one pending
 * proposal carrying a single gmail_send action. Shared by the direct Send path
 * and the AI chat's lazy proposal creation.
 */
export function buildComposeProposalRequest(
  email: ComposedEmail,
  currentUserId: number,
  options?: { placeholders?: boolean }
): CreateProposalRequest {
  const actionParams = buildGmailSendParams(email, options);
  return {
    title: email.subject.trim() || 'Email',
    // Backend ignores assigned_user_id and scopes the proposal to the
    // requester; the TS contract still requires it, so we pass the sender.
    assigned_user_id: currentUserId,
    source_type_name: MANUAL_COMPOSE_SOURCE_TYPE,
    source_id: String(currentUserId),
    source_context: {
      channel: MANUAL_COMPOSE_CHANNEL,
      account_identifier: '',
      entity_ids: email.recipientEntityId ? [email.recipientEntityId] : [],
    },
    actions: [
      {
        execution_order: 0,
        proposed_action_type_name: 'gmail_send',
        payload: { action_params: actionParams },
      },
    ],
  };
}

/** The proposal created by an attempt, tracked across retries. */
interface PendingSendAttempt {
  proposalId: number;
  /** Structural signature of the email content that created this proposal. */
  contentSignature: string;
}

/**
 * Structural signature of the email fields that make up the proposal's
 * content. Two calls with matching signatures are the "same send" for retry
 * purposes; a differing signature means the user edited the email.
 */
function computeContentSignature(email: ComposedEmail): string {
  return JSON.stringify({
    to: email.to,
    subject: email.subject,
    body: email.body,
    cc: email.cc,
    bcc: email.bcc,
    body_html: email.body_html,
    attachment_file_ids: email.attachment_file_ids,
    social_account_id: email.social_account_id,
    disable_tracking: email.disable_tracking,
    recipientEntityId: email.recipientEntityId,
  });
}

/**
 * Returns a stable async sender. Resolves once the email has been accepted for
 * sending (approval succeeded); the send itself may complete asynchronously in
 * the backend worker. Throws on create/update/approve failure so the caller
 * can keep the composer open and surface the error.
 */
export function useSendComposedEmail(
  orgId: number,
  currentUserId: number
): (email: ComposedEmail) => Promise<void> {
  const pendingAttemptRef = useRef<PendingSendAttempt | null>(null);
  // Serializes overlapping sends. The pendingAttemptRef retry-dedup only helps
  // across *sequential* calls — two calls dispatched before either records its
  // created proposal would both read a null ref and each call create(), sending
  // a duplicate email. Holding the in-flight promise here makes a concurrent
  // second call await the first instead of starting its own create/approve.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const runSend = useCallback(
    async (email: ComposedEmail): Promise<void> => {
      const client = new ProposalClient(orgId);

      if (email.proposalId !== undefined && email.actionId !== undefined) {
        // AI chat already holds a pending proposal for this draft: flush the
        // final editor content onto its action, then approve that proposal.
        // Echoing the payload as the stale-guard makes any unnoticed
        // concurrent agent edit fail with 409 STALE_ACTIONS instead of
        // silently sending different content. No orphan cleanup here — the
        // proposal backs the still-open composer draft, so on failure we
        // leave it pending and let the user retry (the backend's
        // approve_and_execute state check stays authoritative).
        const payload = {
          action_params: buildGmailSendParams(email),
        };
        try {
          // updateActionPayload may version the action (a new row with a new
          // id) rather than editing in place — e.g. once the proposal has been
          // idle past the versioning window. Guard approval with the id it
          // returns, not the pre-update email.actionId, or a versioned update
          // leaves the stale-guard pointing at a superseded action and the send
          // never approves.
          const updatedAction = await client.updateActionPayload(
            email.proposalId,
            email.actionId,
            { payload }
          );
          await client.approve(email.proposalId, undefined, [
            { id: updatedAction.id, payload },
          ]);
          logger.warn('[useSendComposedEmail] chat-proposal send succeeded', {
            proposalId: email.proposalId,
            orgId,
          });
          return;
        } catch (sendError) {
          // Ambiguous-approval recovery (parity with the fallback path's
          // pendingAttemptRef reuse): if approve() actually landed on the
          // backend but the response was lost, this proposal is no longer
          // pending — so the retry's updateActionPayload fails against a
          // resolved proposal and approve never re-runs, stranding the
          // composer with no way to retry or confirm delivery. Refetch the
          // authoritative state; if it's already approved or executing, the
          // email is on its way, so treat this as a successful send rather
          // than surfacing a dead-end error.
          let resolution: ProposalResolution | null = null;
          try {
            ({ resolution } = await client.executionStatus(email.proposalId));
          } catch (statusError) {
            logger.warn(
              '[useSendComposedEmail] chat-proposal state refetch failed',
              {
                proposalId: email.proposalId,
                orgId,
                error:
                  statusError instanceof Error
                    ? statusError.message
                    : String(statusError),
              }
            );
          }
          if (resolution === 'approved' || resolution === 'executing') {
            logger.warn(
              '[useSendComposedEmail] chat-proposal already resolved; treating as sent',
              { proposalId: email.proposalId, orgId, resolution }
            );
            return;
          }
          // Still pending (approve genuinely didn't take) or terminally
          // failed — surface the error so the user can retry. A retry re-runs
          // update+approve on the still-pending proposal, which the backend
          // handles as the authoritative source of truth.
          throw sendError;
        }
      }

      const contentSignature = computeContentSignature(email);
      const pending = pendingAttemptRef.current;

      let proposalId: number;

      if (pending && pending.contentSignature === contentSignature) {
        // Same content as the attempt that already created a proposal —
        // reuse it instead of creating a second gmail_send proposal.
        ({ proposalId } = pending);
        logger.warn(
          '[useSendComposedEmail] reusing pending proposal from prior attempt',
          { proposalId, orgId }
        );
      } else {
        if (pending) {
          // The user edited the email before retrying — the tracked
          // proposal no longer reflects what they want sent. Discard it
          // best-effort (outcome doesn't gate whether we proceed) and
          // start fresh so we never approve stale content.
          logger.warn(
            '[useSendComposedEmail] content differs from pending attempt, discarding stale proposal',
            { proposalId: pending.proposalId, orgId }
          );
          try {
            await client.reject(pending.proposalId, {
              reason: 'Automated cleanup: content changed before retry',
            });
          } catch (discardError) {
            logger.warn(
              '[useSendComposedEmail] failed to discard stale proposal',
              {
                proposalId: pending.proposalId,
                orgId,
                error:
                  discardError instanceof Error
                    ? discardError.message
                    : String(discardError),
              }
            );
          }
          pendingAttemptRef.current = null;
        }

        const created = await client.create(
          buildComposeProposalRequest(email, currentUserId)
        );

        proposalId = created.id;
        pendingAttemptRef.current = { proposalId, contentSignature };
        logger.warn(
          '[useSendComposedEmail] tracking pending proposal for retry-safety',
          { proposalId, orgId }
        );
      }

      // The single gmail_send action is created selected (is_selected=True), so
      // approving with no explicit selection executes it.
      try {
        await client.approve(proposalId);
        pendingAttemptRef.current = null;
        logger.warn('[useSendComposedEmail] send succeeded', {
          proposalId,
          orgId,
        });
      } catch (approveError) {
        logger.warn(
          '[useSendComposedEmail] approve failed, rejecting orphaned proposal',
          {
            proposalId,
            orgId,
            error:
              approveError instanceof Error
                ? approveError.message
                : String(approveError),
          }
        );

        // Best-effort cleanup — the composer surfaces the original approve
        // error regardless, so a cleanup failure must not mask it. If the
        // reject itself fails, we genuinely don't know whether the proposal
        // is still pending, already approved, or mid-execution — leave it
        // tracked so a same-content retry reuses this proposal and lets the
        // backend's own approve_and_execute state check be authoritative,
        // instead of blindly creating a second (duplicate) proposal.
        try {
          await client.reject(proposalId, {
            reason: 'Automated cleanup: approval failed after create',
          });
          pendingAttemptRef.current = null;
          logger.warn('[useSendComposedEmail] cleaned up orphaned proposal', {
            proposalId,
            orgId,
          });
        } catch (rejectError) {
          logger.warn(
            '[useSendComposedEmail] failed to clean up orphaned proposal',
            {
              proposalId,
              orgId,
              error:
                rejectError instanceof Error
                  ? rejectError.message
                  : String(rejectError),
            }
          );
        }

        throw approveError;
      }
    },
    [orgId, currentUserId]
  );

  return useCallback(
    (email: ComposedEmail): Promise<void> => {
      // If a send is already running, return its promise so a rapid second
      // invocation joins the first attempt rather than creating/approving a
      // second proposal (duplicate email). The lock clears when the in-flight
      // send settles, whether it resolved or threw.
      if (inFlightRef.current) {
        return inFlightRef.current;
      }
      const promise = runSend(email).finally(() => {
        inFlightRef.current = null;
      });
      inFlightRef.current = promise;
      return promise;
    },
    [runSend]
  );
}
